import React, { useEffect, useState } from 'react'

const initialForm = {
  version: '',
  summary: '',
  changes: '',
  effectiveFrom: '',
}

export default function NormativeGovernancePanel() {
  const [adminSessionReady, setAdminSessionReady] = useState(false)
  const [activeRules, setActiveRules] = useState(null)
  const [updates, setUpdates] = useState([])
  const [audit, setAudit] = useState([])
  const [form, setForm] = useState(initialForm)
  const [actionVersion, setActionVersion] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')
  const [normativeReport, setNormativeReport] = useState('')
  const [reportUpdatedAt, setReportUpdatedAt] = useState('')
  const [diffRows, setDiffRows] = useState([])
  const [approvalNote, setApprovalNote] = useState('')
  const [hasPendingCandidate, setHasPendingCandidate] = useState(false)
  const [isRefreshingCandidate, setIsRefreshingCandidate] = useState(false)
  const [isApprovingCandidate, setIsApprovingCandidate] = useState(false)

  const getBearerHeaders = () => {
    const token = localStorage.getItem('adminToken') || ''
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
  }

  const loadData = async (forceAdminSession = false) => {
    setError('')
    try {
      const [a, u, l] = await Promise.all([
        fetch('/api/normative/rules/active'),
        fetch('/api/normative/updates?limit=20'),
        fetch('/api/normative/audit?limit=30'),
      ])
      const active = await a.json()
      const upd = await u.json()
      const log = await l.json()
      setActiveRules(active?.data || null)
      setUpdates(Array.isArray(upd?.data) ? upd.data : [])
      setAudit(Array.isArray(log?.data) ? log.data : [])

      if (adminSessionReady || forceAdminSession) {
        const authHeaders = getBearerHeaders()
        const [reportRes, diffRes] = await Promise.all([
          fetch('/api/admin/normative-updates/report', { headers: authHeaders }),
          fetch('/api/admin/normative-updates/diff', { headers: authHeaders }),
        ])
        if (reportRes.status === 401 || diffRes.status === 401) {
          setAdminSessionReady(false)
          setDiffRows([])
          setNormativeReport('')
          setReportUpdatedAt('')
          setHasPendingCandidate(false)
          setError('Sesion admin invalida o expirada. Inicia sesion admin nuevamente.')
          return
        }
        const reportData = await reportRes.json()
        const diffData = await diffRes.json()
        if (reportRes.ok && reportData?.success) {
          setNormativeReport(reportData?.data?.raw || '')
          setReportUpdatedAt(reportData?.data?.updatedAt || '')
        } else if (reportRes.status === 404) {
          setNormativeReport('Sin candidato pendiente para mostrar.')
          setReportUpdatedAt('')
        }
        if (diffRes.ok && diffData?.success) {
          setDiffRows(Array.isArray(diffData?.data?.diffBySeries) ? diffData.data.diffBySeries : [])
        } else if (diffRes.status === 404) {
          setDiffRows([])
        }

        const pending = diffRes.ok && diffData?.success && (Array.isArray(diffData?.data?.diffBySeries) ? diffData.data.diffBySeries.some((row) => (row.added?.length || 0) > 0 || (row.modified?.length || 0) > 0) : false)
        setHasPendingCandidate(Boolean(pending))
        setMsg(`Sesion admin validada. Series evaluadas: ${Array.isArray(diffData?.data?.diffBySeries) ? diffData.data.diffBySeries.length : 0}. Pendiente: ${pending ? 'si' : 'no'}.`)
      }
    } catch (e) {
      setError(e?.message || 'No se pudo cargar la gobernanza normativa')
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const connectAdminSession = async () => {
    setMsg('')
    setError('')
    const token = localStorage.getItem('adminToken') || ''
    if (!token) {
      setError('No hay sesion admin activa. Inicia sesion desde Admin Login.')
      return
    }
    setAdminSessionReady(true)
    await loadData(true)
  }

  const runAction = async (path, body = null) => {
    setMsg('')
    setError('')
    try {
      const authHeaders = getBearerHeaders()
      const resp = await fetch(path, {
        method: 'POST',
        headers: authHeaders,
        body: body ? JSON.stringify(body) : undefined,
      })
      const data = await resp.json()
      if (!resp.ok || !data?.success) throw new Error(data?.error || 'Operacion rechazada')
      setMsg(data?.message || `Operacion ejecutada en ${path}`)
      await loadData()
    } catch (e) {
      setError(e?.message || 'Error ejecutando accion')
    }
  }

  const publishVersion = async () => {
    const changes = form.changes
      .split('\n')
      .map((x) => x.trim())
      .filter(Boolean)
    await runAction('/api/normative/rules/publish', {
      version: form.version,
      summary: form.summary,
      changes,
      effectiveFrom: form.effectiveFrom || new Date().toISOString(),
    })
  }

  const approveNormativeCandidate = async () => {
    if (isApprovingCandidate) return
    setMsg('')
    setError('')
    setIsApprovingCandidate(true)
    try {
      const resp = await fetch('/api/admin/normative-updates/approve', {
        method: 'POST',
        headers: getBearerHeaders(),
        body: JSON.stringify({ note: approvalNote }),
      })
      const data = await resp.json()
      if (!resp.ok || !data?.success) throw new Error(data?.error || 'No se pudo aprobar candidato')
      setMsg(`✅ ${data?.message || 'Candidato normativo aprobado'}. Se limpio la cola de candidatos pendientes.`)
      await loadData()
    } catch (e) {
      setError(e?.message || 'Error aprobando candidato')
    } finally {
      setIsApprovingCandidate(false)
    }
  }

  const refreshNormativeCandidate = async () => {
    if (isRefreshingCandidate) return
    setMsg('')
    setError('')
    setIsRefreshingCandidate(true)
    try {
      const resp = await fetch('/api/admin/normative-updates/refresh-candidate', {
        method: 'POST',
        headers: getBearerHeaders(),
        body: JSON.stringify({ note: 'refresh desde panel admin' }),
      })
      const data = await resp.json()
      if (!resp.ok || !data?.success) throw new Error(data?.error || 'No se pudo refrescar candidato')
      setMsg(data?.message || 'Candidato actualizado')
      await loadData()
    } catch (e) {
      setError(e?.message || 'Error refrescando candidato')
    } finally {
      setIsRefreshingCandidate(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
        <h3 className="text-sm font-semibold text-cyan-100">Gobernanza Normativa (Single-Admin)</h3>
        <p className="mt-1 text-xs text-slate-300">Flujo: draft - reviewed - approved - activated. Requiere sesion admin autenticada por token.</p>
        <div className="mt-2 flex items-center gap-2 text-[11px]">
          <span className={`rounded px-2 py-1 font-semibold ${hasPendingCandidate ? 'bg-amber-950 text-amber-300' : 'bg-emerald-950 text-emerald-300'}`}>
            {hasPendingCandidate ? 'Semaforo: Pendiente' : 'Semaforo: Sin pendiente'}
          </span>
          <span className="rounded bg-slate-800 px-2 py-1 text-slate-300">Reporte: {reportUpdatedAt ? 'actualizado' : 'sin carga'}</span>
        </div>
        <div className="mt-3 max-w-sm">
          <label className="mb-1 block text-xs text-slate-300">Sesion admin</label>
          <button type="button" onClick={connectAdminSession} className="mt-2 rounded bg-cyan-700 px-3 py-2 text-xs font-semibold text-white hover:bg-cyan-600">Validar sesion activa</button>
        </div>
        {msg && <p className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-2 text-sm font-semibold text-emerald-800">{msg}</p>}
        {error && <p className="mt-2 rounded border border-rose-900 bg-rose-950 px-2 py-2 text-xs font-semibold text-rose-300">{error}</p>}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">Publicar nueva version</h4>
          <div className="mt-3 space-y-2">
            <input className="h-9 w-full rounded border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100" placeholder="Version (ej: v1.4.0)" value={form.version} onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))} />
            <input className="h-9 w-full rounded border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100" placeholder="Resumen" value={form.summary} onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))} />
            <input className="h-9 w-full rounded border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100" placeholder="Vigencia ISO (opcional)" value={form.effectiveFrom} onChange={(e) => setForm((p) => ({ ...p, effectiveFrom: e.target.value }))} />
            <textarea className="h-24 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100" placeholder="Cambios (1 por linea)" value={form.changes} onChange={(e) => setForm((p) => ({ ...p, changes: e.target.value }))} />
            <button type="button" onClick={publishVersion} className="rounded bg-cyan-700 px-3 py-2 text-xs font-semibold text-white">Publicar draft</button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">Cambiar estado / rollback</h4>
          <div className="mt-3 space-y-2">
            <input className="h-9 w-full rounded border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100" placeholder="Version objetivo" value={actionVersion} onChange={(e) => setActionVersion(e.target.value)} />
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => runAction(`/api/normative/rules/review/${actionVersion}`)} className="rounded border border-slate-300 px-3 py-1.5 text-xs">Review</button>
              <button type="button" onClick={() => runAction(`/api/normative/rules/approve/${actionVersion}`)} className="rounded border border-slate-300 px-3 py-1.5 text-xs">Approve</button>
              <button type="button" onClick={() => runAction(`/api/normative/rules/activate/${actionVersion}`)} className="rounded border border-slate-300 px-3 py-1.5 text-xs">Activate</button>
              <button type="button" onClick={() => runAction(`/api/normative/rules/rollback/${actionVersion}`)} className="rounded border border-amber-300 px-3 py-1.5 text-xs text-amber-800">Rollback</button>
            </div>
            <input className="h-9 w-full rounded border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100" placeholder="Motivo rechazo" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
            <button type="button" onClick={() => runAction(`/api/normative/rules/reject/${actionVersion}`, { reason: rejectReason })} className="rounded border border-rose-300 px-3 py-1.5 text-xs text-rose-700">Reject</button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">Version activa</h4>
          <p className="mt-2 text-sm text-slate-200">{activeRules?.version || activeRules?.id || 'Sin version activa'}</p>
          {activeRules?.effectiveFrom && <p className="text-xs text-slate-400">Vigencia: {activeRules.effectiveFrom}</p>}
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">Updates normativos recientes</h4>
          <div className="mt-2 max-h-40 overflow-auto text-xs text-slate-300">
            {updates.slice(0, 12).map((u) => <p key={u.id}>- {u.type || 'normativa'} | {u.title || u.id} | {u.status || 'draft'}</p>)}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
        <h4 className="text-sm font-semibold text-cyan-100">Bitacora de gobernanza</h4>
        <div className="mt-2 max-h-56 overflow-auto text-xs text-slate-300">
          {audit.map((e) => <p key={e.id}>- {e.action || 'evento'} | {e.version || e.updateId || 'N/D'} | {e.toStatus || 'N/D'}</p>)}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">Reporte de actualizacion automatica</h4>
          <p className="mt-1 text-xs text-slate-300">Generado por `normative:update`. Ultima copia: {reportUpdatedAt || 'N/D'}</p>
          <pre className="mt-3 max-h-72 overflow-auto rounded border border-slate-700 bg-slate-950 p-2 text-[11px] text-slate-300">{normativeReport || 'Sin reporte disponible. Ejecuta el pipeline de actualizacion.'}</pre>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">Diff y aprobacion</h4>
          <div className="mt-3 max-h-48 overflow-auto rounded border border-slate-700 bg-slate-950/70">
            <table className="w-full text-xs">
              <thead className="bg-slate-950 text-slate-400">
                <tr>
                  <th className="px-2 py-1 text-left">Serie</th>
                  <th className="px-2 py-1 text-right">Nuevos</th>
                  <th className="px-2 py-1 text-right">Modif.</th>
                  <th className="px-2 py-1 text-right">Huecos</th>
                </tr>
              </thead>
              <tbody>
                {diffRows.map((row) => (
                  <tr key={row.key} className="border-t border-slate-800">
                    <td className="px-2 py-1">{row.key}</td>
                    <td className="px-2 py-1 text-right">{row.added?.length || 0}</td>
                    <td className="px-2 py-1 text-right">{row.modified?.length || 0}</td>
                    <td className="px-2 py-1 text-right">{row.missing?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <textarea className="mt-3 h-20 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100" placeholder="Motivo de aprobacion (auditoria)" value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} />
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={refreshNormativeCandidate} disabled={isRefreshingCandidate || isApprovingCandidate} className="rounded bg-slate-700 px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-500">{isRefreshingCandidate ? 'Refrescando...' : 'Refrescar candidato'}</button>
          <button type="button" onClick={approveNormativeCandidate} disabled={!hasPendingCandidate || isApprovingCandidate || isRefreshingCandidate} className="rounded bg-emerald-700 px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300">{isApprovingCandidate ? 'Aplicando...' : 'Aprobar y aplicar candidato'}</button>
          </div>
          {!hasPendingCandidate && <p className="mt-2 text-xs text-slate-600">No hay candidato pendiente de aprobacion.</p>}
        </div>
      </div>
    </div>
  )
}
